export { normalizeDomain } from "@auger/domains"

export type HarmonicRefKind = "company" | "person"

export const HARMONIC_KIND_SOURCE_TYPE: Record<HarmonicRefKind, string> = {
  company: "harmonic_organization",
  person: "harmonic_person",
}

export const HARMONIC_KIND_RECORD_TYPE: Record<HarmonicRefKind, string> = {
  company: "organization",
  person: "person",
}

export interface HarmonicEntityRef {
  urn: string
  kind: HarmonicRefKind
  displayName: string
  role?: string
}

/** Only company/person URNs become records; tags, schools, enrichment and
 *  compound (parenthesised) URNs are ignored. */
export function parseKind(value: unknown): HarmonicRefKind | null {
  if (typeof value !== "string") return null
  if (value.startsWith("urn:harmonic:company:")) return "company"
  if (value.startsWith("urn:harmonic:person:")) return "person"
  return null
}

// The payload is Harmonic's raw JSON, read defensively: any level may be missing,
// null or a different shape than documented.
const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object"
const field = (value: unknown, key: string): unknown => (isObject(value) ? value[key] : undefined)
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])
const text = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined

export function extractReferencedEntities(
  payload: unknown,
  type: "organization" | "person"
): HarmonicEntityRef[] {
  if (!isObject(payload)) return []
  const out: HarmonicEntityRef[] = []
  const push = (value: unknown, displayName = "", role?: string) => {
    const kind = parseKind(value)
    if (!kind || typeof value !== "string") return
    out.push({ urn: value, kind, displayName, role })
  }

  if (type === "organization") {
    for (const inv of list(field(payload.funding, "investors"))) {
      push(field(inv, "entity_urn"), text(field(inv, "name")))
    }
    for (const p of list(payload.people)) {
      push(field(p, "person"), "", text(field(p, "title")) ?? text(field(p, "role_type")))
    }
    for (const urn of list(payload.leadership_prior_companies)) {
      push(urn)
    }
    const rc = payload.related_companies
    if (isObject(rc)) {
      push(field(rc.acquired_by, "company_urn"))
      push(field(rc.subsidiary_of, "company_urn"))
      for (const a of list(rc.acquisitions)) push(field(a, "company_urn"))
      for (const s of list(rc.subsidiaries)) push(field(s, "company_urn"))
    }
  } else {
    for (const exp of list(payload.experience)) {
      push(field(exp, "company"), "", text(field(exp, "title")))
    }
    for (const urn of list(payload.current_company_urns)) {
      push(urn)
    }
  }

  // Dedupe by urn: keep the first occurrence; upgrade to a later duplicate only if the stored entry has no displayName and the duplicate does.
  const byUrn = new Map<string, HarmonicEntityRef>()
  for (const ref of out) {
    const existing = byUrn.get(ref.urn)
    if (!existing) byUrn.set(ref.urn, ref)
    else if (!existing.displayName && ref.displayName) byUrn.set(ref.urn, ref)
  }
  return [...byUrn.values()]
}

/** A key-person entry from an org payload's `people[]` (URN + role context, no name). */
export interface HarmonicKeyPerson {
  person: string
  title?: string
  role_type?: string
  department?: string
  is_current_position?: boolean
}

// Lower rank = more prominent. Leadership first; everything else mid-pack.
const KEY_PEOPLE_ROLE_RANK: Record<string, number> = {
  FOUNDER: 0,
  EXECUTIVE: 1,
  BOARD_MEMBER: 2,
  ADVISER: 3,
  ADVISOR: 3,
}

/**
 * Pick + order the org's key people for display: dedupe by URN, prioritize
 * resolved (we hold a record) → current positions → leadership roles, and cap.
 * `people[]` can hold 300+ entries, most unresolved and role-only, so ordering
 * surfaces the useful ones first.
 */
export function selectKeyPeople(
  people: HarmonicKeyPerson[] | null | undefined,
  resolvedUrns: Set<string>,
  limit = 12
): HarmonicKeyPerson[] {
  const score = (p: HarmonicKeyPerson) =>
    (resolvedUrns.has(p.person) ? 0 : 1000) +
    (p.is_current_position ? 0 : 100) +
    (KEY_PEOPLE_ROLE_RANK[p.role_type ?? ""] ?? 50)
  const byUrn = new Map<string, HarmonicKeyPerson>()
  for (const p of people ?? []) {
    if (typeof p?.person !== "string" || !p.person.startsWith("urn:harmonic:person:")) continue
    const existing = byUrn.get(p.person)
    if (!existing || score(p) < score(existing)) byUrn.set(p.person, p)
  }
  return [...byUrn.values()].sort((a, b) => score(a) - score(b)).slice(0, limit)
}

/**
 * `employee_highlights` text repeats its own category ("<category>, <name>: …"),
 * so rendering "<category>: <text>" duplicates it. Strip a leading category (and
 * any trailing separator) from the text so the category label isn't shown twice.
 */
export function stripHighlightCategory(
  text: string | null | undefined,
  category: string | null | undefined
): string {
  const t = (text ?? "").trim()
  const c = (category ?? "").trim()
  if (!c || !t.toLowerCase().startsWith(c.toLowerCase())) return t
  return t
    .slice(c.length)
    .replace(/^[\s,:]+/, "")
    .trim()
}
